import { memo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import Grid from '@mui/system/Grid'

import { useGetMiscItemsQuery } from '@/app/services/miscItems'
import { useGetQuoteItemsQuery } from '@/app/services/quoteItems'
import { useGetQuoteQuery } from '@/app/services/quotes'
import { setSelectedCustomer, setSelectedCustomerId } from '@/app/slices/customersSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { TbxQuoteHeaderSkeleton, TbxQuoteItemListSkeleton } from '@/common/components'
import { useToolBoxTreatments } from '@/common/hooks'
import { useFeatureFlags } from '@/common/hooks/useFeatureFlags'
import { Paths } from '@/common/utils'
import { QUOTE_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

import MiscellaneousItemList from '../components/MiscellaneousItemList/MiscellaneousItemList'
import MiscItemListActions from '../components/MiscellaneousItemList/MiscItemListActions'
import QuoteAttachmentsPanel from '../components/QuoteAttachments/QuoteAttachmentsPanel'
import QuoteHeader from '../components/QuoteHeader/QuoteHeader'
import QuoteItemList from '../components/QuoteItemList/QuoteItemList'
import QuoteItemListActions from '../components/QuoteItemList/QuoteItemListActions'
import QuoteReview from '../components/QuoteReview/QuoteReview'
import QuoteSummary from '../components/QuoteSummary/QuoteSummary'
import QuoteToolbar from '../components/QuoteToolbar/QuoteToolbar'

const classes = {
    section: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${blueGrey[200]}`,
        width: '100%',
    },
    content: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${blueGrey[200]}`,
        width: '100%',
        padding: 3,
    },
    empty: {
        paddingBlock: 14,
        height: 'calc(100vh - 385px)',
    },
}

const Quote = () => {
    const { t } = useTranslation()
    const { quoteId } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { showMiscItems } = useToolBoxTreatments()
    const { showQuoteAttachments } = useFeatureFlags()

    const organisationId = useSelector(selectOrganisationId)

    const {
        data: quote,
        isLoading,
        isSuccess,
    } = useGetQuoteQuery({
        organisationId,
        quoteId,
    })
    const {
        data: quoteItems,
        error,
        isError,
        isLoading: isLoadingItems,
    } = useGetQuoteItemsQuery({
        organisationId,
        quoteId,
    })

    const { data: miscItems, isLoading: isLoadingMiscItems } = useGetMiscItemsQuery({
        organisationId,
        quoteId,
    })

    const isQuoteEditable = [QUOTE_STATUS_ARRAY.NotCalculated, QUOTE_STATUS_ARRAY.Calculated].includes(quote?.status)

    useEffect(() => {
        if (!isLoading && isError && error?.status >= 400 && error?.status < 500 && error?.status !== 401) {
            navigate(Paths.NOT_FOUND)
        }

        if (!isLoading && isError && error?.status >= 500) {
            navigate(Paths.ERROR_PATHNAME)
        }
    }, [isLoading, isError, error?.status, navigate])

    // Cleanup effect: clear quote-specific customer selection on unmount
    useEffect(() => {
        return () => {
            dispatch(setSelectedCustomerId(null))
            dispatch(setSelectedCustomer(null))
        }
    }, [dispatch])

    // if (!isLoading && isSuccess) {
    return (
        <>
            <QuoteToolbar
                hasMiscItems={Boolean(miscItems?.length)}
                hasQuoteParts={Boolean(quoteItems?.length)}
                key={`${quote?.id}-toolbar`}
            />
            <Grid
                alignContent="flex-start"
                component="section"
                maxWidth={'xl'}
                size={12}
                container
            >
                {isLoading ? <TbxQuoteHeaderSkeleton /> : <QuoteHeader key={`${quote?.id}-header`} />}

                {isLoadingItems ? (
                    <Box sx={classes.content}>
                        <TbxQuoteItemListSkeleton />
                    </Box>
                ) : null}

                {!isLoadingItems && !quoteItems?.length && !miscItems?.length ? (
                    <Box
                        sx={[
                            {
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            },
                            classes.content,
                            classes.empty,
                        ]}
                    >
                        <Box
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                component="h2"
                                variant="h6"
                            >
                                {t('No parts in this $t(quote)')}
                            </Typography>
                            <Typography>{t('Please add your parts, select the material and get a quote.')}</Typography>
                        </Box>
                    </Box>
                ) : null}

                {isQuoteEditable && !isLoadingItems && Boolean(quoteItems?.length) ? (
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                flexDirection: 'column',
                            },
                            classes.content,
                        ]}
                    >
                        <Typography
                            sx={{
                                mx: 1.5,
                                textTransform: 'uppercase',
                            }}
                            variant="strong1"
                        >
                            {t('Parts')}
                        </Typography>
                        <QuoteItemListActions key={`${quote?.id}-item-actions`} />
                        <QuoteItemList
                            key={`${quote?.id}-item-list`}
                            showAttachments={showQuoteAttachments}
                        />
                    </Box>
                ) : null}

                {showMiscItems && isQuoteEditable && !isLoadingMiscItems && Boolean(miscItems?.length) ? (
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                flexDirection: 'column',
                                mt: 3,
                            },
                            classes.content,
                        ]}
                    >
                        <Typography
                            sx={{
                                mx: 1.5,
                                textTransform: 'uppercase',
                            }}
                            variant="strong1"
                        >
                            {t('Miscellaneous items')}
                        </Typography>
                        <MiscItemListActions key={`${quote?.id}-misc-item-actions`} />
                        <MiscellaneousItemList
                            key={`${quote?.id}-misc-item-list`}
                            showAttachments={showQuoteAttachments}
                        />
                    </Box>
                ) : null}

                {!isQuoteEditable && !isLoadingItems && (Boolean(quoteItems?.length) || Boolean(miscItems?.length)) ? (
                    <QuoteReview key={`${quote?.id}-review`} />
                ) : null}

                {showQuoteAttachments && isSuccess ? (
                    <Box
                        sx={[
                            {
                                display: 'flex',
                                flexDirection: 'column',
                                mt: 3,
                            },
                            classes.content,
                        ]}
                    >
                        <QuoteAttachmentsPanel
                            key={`${quote?.id}-attachments`}
                            organisationId={organisationId}
                            quoteId={quoteId}
                        />
                    </Box>
                ) : null}
            </Grid>

            {!isLoadingItems && (Boolean(quoteItems?.length) || Boolean(miscItems?.length)) ? (
                <Grid
                    component="aside"
                    maxWidth={'xl'}
                    mb={4}
                    size={12}
                >
                    <QuoteSummary key={`${quote?.id}-summary`} />
                </Grid>
            ) : null}
        </>
    )
    // }
}
export default memo(Quote)
